import { styled } from "sharedUI/styles";
import { isUserData, UserDataAndState } from "common/Database";
import { LoadingIndicator } from "designer/apps/orgs/orgs-styles";
import { History } from "history";
import * as React from "react";
import { useSelector } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { RootState } from "store/types";
import InputLabel from "designer/ui/smart/input-label/input-label";
import { GlideDropdown } from "designer/ui/smart/glide-dropdown/glide-dropdown";
import { Button } from "designer/ui/smart/button/button-style";
import { Range } from "common/firebase-function-types";
import { createOrg } from "designer/apps/orgs/orgs-lib";
import { trackEvent } from "analytics";
import * as routes from "../../../../../routes";
import { OrgIcon } from "designer/apps/orgs/orgs-sidebar/org-icon";
import CreatedTeam from "./created-team";

interface Props extends RouteComponentProps {
    onComplete: () => void;
    history: History;
}

export const CreateTeamModalStyle = styled.div`
    width: 480px;
    padding: 58px 48px;
`;

const CreateTeamStyle = styled.div`
    display: flex;
    flex-direction: column;

    small {
        font-size: 14px;
        color: ${p => p.theme.n700A};
    }

    > hgroup {
        margin-bottom: 12px;

        h1 {
            font-size: 26px;
            font-weight: 800;
            margin-top: 12px;
        }
    }

    > .field {
        display: flex;
        flex-direction: column;
        margin: 8px 0 0;
        font-size: 14px;

        label {
            font-weight: 600;
            color: ${p => p.theme.n800A};
        }
    }

    .continue button {
        width: 100%;
        margin: 12px 0 0 0;
        color: ${p => p.theme.n0};
        background-color: ${p => p.theme.accentPrimary};
    }
`;

export const companySizes: Record<string, Range | undefined> = {
    Select: undefined,
    "Not using it for work": { min: 0, max: 0 },
    "1-50 employees": { min: 1, max: 50 },
    "51-200 employees": { min: 51, max: 200 },
    "201-1000 employees": { min: 201, max: 1000 },
};

const CreateTeam: React.FunctionComponent<Props> = p => {
    const userData: UserDataAndState = useSelector((state: RootState) => state.userData);
    const [teamName, setTeamName] = React.useState("");
    const [companySize, setCompanySize] = React.useState("Select");
    const [submitted, setSubmitted] = React.useState(false);
    const [newOrgID, setNewOrgID] = React.useState<string | undefined>(undefined);

    const onCreateOrg = React.useCallback(async () => {
        const companySizeValue = companySizes[companySize];
        if (companySizeValue === undefined) {
            return;
        }
        const details = {
            name: teamName,
            numEmployees: companySizeValue,
        };
        setSubmitted(true);
        const newID = await createOrg(details);

        trackEvent("org_created", {
            org_id: newID,
            ...details,
        });

        p.history.push(routes.org(newID));
        setNewOrgID(newID);
    }, [companySize, teamName, p.history]);

    if (!isUserData(userData)) {
        return <LoadingIndicator />;
    }

    if (newOrgID !== undefined) {
        return <CreatedTeam orgID={newOrgID} onDone={p.onComplete} />;
    }

    return (
        <CreateTeamStyle>
            <hgroup>
                <OrgIcon size={"xlarge"} label={teamName} />
                <h1>Create a new team</h1>
            </hgroup>

            <div className="field">
                <label>Team Name</label>
                <InputLabel
                    margin={8}
                    autoFocus={true}
                    focusBorder={true}
                    label={undefined}
                    placeholder={teamName}
                    onChange={e => setTeamName(e.target.value)}
                    tallerVariation={true}
                />
            </div>

            <div className="field">
                <label>Company Size</label>
                <GlideDropdown
                    items={Object.keys(companySizes)}
                    descriptionForItem={i => i}
                    selected={companySize}
                    onItemSelect={setCompanySize}
                    tallerVariation={true}
                />
            </div>
            <div className="continue">
                <Button
                    disabled={teamName === "" || companySize === "Select" || submitted}
                    onClick={onCreateOrg}
                    tallerVariation={true}>
                    {submitted ? `Creating your team...` : `Continue`}
                </Button>
            </div>
        </CreateTeamStyle>
    );
};

export default withRouter(CreateTeam);
